/* eslint-disable react-hooks/exhaustive-deps */
import { DeleteOutlined, CopyOutlined, EyeOutlined, UploadOutlined } from "@ant-design/icons";
import {
  Table,
  Space,
  Button,
  Popconfirm,
  message,
  Upload,
  Modal,
  Form,
  Input,
  Tooltip,
  theme
} from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useState } from "react";

import { copyTextToClipboard } from "../../../utils/clipboardHelper";
import { showErrorNotification } from "../../../utils/notification";
import { clamp } from "../../../utils/responsiveHelpers";

const MailingAssetManager = ({ ROOT_URL }) => {
  const TARGET_URL = "admin/bulk-notifications/mailing-asset";

  const {
    token: { colorFillTertiary, colorTextSecondary }
  } = theme.useToken();

  const [assetsList, setAssetsList] = useState([]);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadForm] = Form.useForm();
  const [previewModal, setPreviewModal] = useState({
    open: false,
    asset: null
  });
  const [metadataForm] = Form.useForm();
  const [metadataModal, setMetadataModal] = useState({
    open: false,
    asset: null
  });

  // Fetch assets on mount
  useEffect(() => {
    fetchAssets();
  }, []);

  const fetchAssets = () => {
    setTableLoading(true);
    axios
      .get(`${ROOT_URL}/${TARGET_URL}/list`)
      .then((res) => {
        const { assets } = res.data;
        // Add index to assets for table key
        const indexedAssets = assets.map((asset, idx) => ({
          ...asset,
          index: idx + 1
        }));
        setAssetsList(indexedAssets);
      })
      .catch((err) => {
        showErrorNotification(err);
      })
      .finally(() => {
        setTableLoading(false);
      });
  };

  const handleUploadAsset = async (file) => {
    // Don't upload immediately, just store the file and open modal
    setUploadFile(file);
    setUploadModalOpen(true);
    return false; // Prevent default upload behavior
  };

  const handleSubmitUpload = async () => {
    if (!uploadFile) return;

    setUploadLoading(true);
    try {
      const { description } = await uploadForm.validateFields();
      const fd = new FormData();
      fd.append("file", uploadFile);
      fd.append("description", description);

      await axios.post(`${ROOT_URL}/${TARGET_URL}/upload`, fd);
      message.success(`${uploadFile.name} uploaded successfully!`);

      // Reset form and modal
      setUploadModalOpen(false);
      uploadForm.resetFields();
      setUploadFile(null);

      // Refresh assets list
      await fetchAssets();
    } catch (err) {
      showErrorNotification(err);
    } finally {
      setUploadLoading(false);
    }
  };

  const handleDeleteAsset = (assetId) => {
    axios
      .delete(`${ROOT_URL}/${TARGET_URL}/${assetId}`)
      .then(() => {
        message.success("Asset deleted successfully");
        fetchAssets();
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const copyToClipboard = async (url) => {
    const hasCopied = await copyTextToClipboard(url);
    if (!hasCopied) {
      message.error("Failed to copy URL");
      return;
    }
    message.success("URL copied to clipboard!");
  };

  const openPreviewModal = (asset) => {
    setPreviewModal({
      open: true,
      asset
    });
  };

  const openMetadataModal = (asset) => {
    metadataForm.setFieldsValue({
      description: asset.description || ""
    });
    setMetadataModal({
      open: true,
      asset
    });
  };

  const handleUpdateMetadata = () => {
    metadataForm.validateFields().then((values) => {
      const { description } = values;

      axios
        .post(`${ROOT_URL}/${TARGET_URL}/${metadataModal.asset.assetId}/update-metadata`, {
          description
        })
        .then(() => {
          message.success("Metadata updated");
          setMetadataModal({ open: false, asset: null });
          fetchAssets();
        })
        .catch((err) => {
          showErrorNotification(err);
        });
    });
  };

  const columns = [
    {
      title: "S.No",
      dataIndex: "index",
      key: "index",
      width: 60
    },
    {
      title: "Asset Name",
      dataIndex: "displayName",
      key: "displayName",
      sorter: (a, b) => a.displayName.localeCompare(b.displayName)
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (desc) => desc || "-",
      width: 200
    },
    {
      title: "Created By",
      dataIndex: "createdBy",
      key: "createdBy",
      render: (text) => text || "-"
    },
    {
      title: "Uploaded",
      dataIndex: "uploadedAt",
      key: "uploadedAt",
      render: (date) => dayjs(date).format("DD MMM YYYY | HH:mm"),
      sorter: (a, b) => new Date(a.uploadedAt) - new Date(b.uploadedAt)
    },

    {
      title: "Actions",
      key: "action",
      width: 280,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Preview">
            <Button
              type="text"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => openPreviewModal(record)}
            />
          </Tooltip>

          <Tooltip title="Copy Link">
            <Button
              type="text"
              icon={<CopyOutlined />}
              size="small"
              onClick={() => copyToClipboard(record.assetUrl)}
            />
          </Tooltip>

          <Tooltip title="Edit Metadata">
            <Button type="text" onClick={() => openMetadataModal(record)} size="small">
              Edit
            </Button>
          </Tooltip>

          <Popconfirm
            title="Delete Asset"
            description="Are you sure you want to delete this asset?"
            onConfirm={() => handleDeleteAsset(record.assetId)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: clamp(12, 20) }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
          flexWrap: "wrap",
          gap: "10px"
        }}
      >
        <h2>Email Marketing Assets</h2>
        <Upload
          beforeUpload={handleUploadAsset}
          multiple={false}
          showUploadList={false}
          accept="image/*"
        >
          <Button type="primary" icon={<UploadOutlined />} loading={uploadLoading}>
            Upload Image
          </Button>
        </Upload>
      </div>

      <Table
        columns={columns}
        dataSource={assetsList}
        loading={tableLoading}
        pagination={assetsList.length > 10 ? { pageSize: 10 } : false}
        rowKey="assetId"
        size="small"
        scroll={{ x: "max-content" }}
      />

      {/* Preview Modal */}
      <Modal
        title="Preview Asset"
        open={previewModal.open}
        onCancel={() => setPreviewModal({ open: false, asset: null })}
        footer={null}
        centered
      >
        {previewModal.asset && (
          <div style={{ textAlign: "center" }}>
            <img
              src={previewModal.asset.assetUrl}
              alt={previewModal.asset.filename}
              style={{
                maxWidth: "100%",
                maxHeight: "400px",
                marginBottom: "16px"
              }}
            />
            <div
              style={{
                backgroundColor: colorFillTertiary,
                padding: "12px",
                borderRadius: "4px",
                wordBreak: "break-all"
              }}
            >
              <p style={{ margin: "0 0 8px 0", fontSize: "12px", color: colorTextSecondary }}>
                Shareable Link:
              </p>
              <p style={{ margin: 0, fontSize: "14px", fontFamily: "monospace" }}>
                {previewModal.asset.assetUrl}
              </p>
            </div>
            <Button
              type="primary"
              onClick={() => copyToClipboard(previewModal.asset.assetUrl)}
              style={{ marginTop: "12px" }}
              icon={<CopyOutlined />}
            >
              Copy Link
            </Button>
          </div>
        )}
      </Modal>

      {/* Metadata Edit Modal */}
      <Modal
        title="Edit Asset Metadata"
        open={metadataModal.open}
        onCancel={() => setMetadataModal({ open: false, asset: null })}
        onOk={handleUpdateMetadata}
        centered
      >
        <Form form={metadataForm} layout="vertical" style={{ marginTop: "20px" }}>
          <Form.Item
            label="Description"
            name="description"
            rules={[{ max: 200, message: "Description cannot exceed 200 characters" }]}
          >
            <Input.TextArea rows={3} placeholder="Enter asset description" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Upload Modal */}
      <Modal
        title="Upload Image Asset"
        open={uploadModalOpen}
        onCancel={() => {
          setUploadModalOpen(false);
          uploadForm.resetFields();
          setUploadFile(null);
        }}
        onOk={handleSubmitUpload}
        centered
        okButtonProps={{ loading: uploadLoading }}
      >
        <Form form={uploadForm} layout="vertical" style={{ marginTop: "20px" }}>
          {uploadFile && (
            <div
              style={{
                marginBottom: "16px",
                padding: "12px",
                backgroundColor: colorFillTertiary,
                borderRadius: "4px"
              }}
            >
              <p style={{ margin: "0 0 8px 0", fontSize: "12px", color: colorTextSecondary }}>
                Selected File:
              </p>
              <p style={{ margin: 0, fontWeight: 500 }}>{uploadFile.name}</p>
            </div>
          )}

          <Form.Item
            label="Description"
            name="description"
            rules={[
              { required: true, message: "Please enter a description" },
              { max: 200, message: "Description cannot exceed 200 characters" }
            ]}
          >
            <Input.TextArea rows={3} placeholder="Enter asset description" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MailingAssetManager;
